/**
 * ALTIORA Growth Radar — Home proactive.
 *
 * « Le professionnel ne cherche plus ses sujets.
 *   ALTIORA détecte les sujets qui méritent sa prise de parole. »
 *
 * Affiche à l'ouverture :
 *  - Opportunity of the Day (hero)
 *  - Grille d'opportunités scorées (top 20)
 *  - Filtres par priorité / badge / catégorie
 *  - Panneau providers (état des sources)
 */
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import {
  Radar, Sparkles, RefreshCw, TrendingUp, ShieldCheck,
  Eye, X, ExternalLink, ChevronRight, Zap, Info,
} from "lucide-react";

const NAVY = "#0a2540";
const INK = "#0a2540";
const SLATE = "#4a5568";
const CHAMPAGNE = "#c9a34e";

// ── Badges d'origine (spec user : 🟢 LIVE / 🔵 KNOWLEDGE / 🟡 À VÉRIFIER / ⚪ DEMO)
const BADGES = {
  live:        { emoji: "🟢", label: "LIVE",       color: "#0a7a3d", bg: "#0a7a3d1a" },
  knowledge:   { emoji: "🔵", label: "KNOWLEDGE",  color: "#1e40af", bg: "#1e40af1a" },
  a_verifier:  { emoji: "🟡", label: "À VÉRIFIER", color: "#a16207", bg: "#a162071a" },
  demo:        { emoji: "⚪", label: "DEMO",       color: "#6b7280", bg: "#6b72801a" },
};

const PRIORITY = {
  prioritaire:  { label: "PRIORITAIRE",  color: "#c92a2a", bg: "#c92a2a1a" },
  important:    { label: "IMPORTANT",    color: "#c9a34e", bg: "#c9a34e1a" },
  a_surveiller: { label: "À SURVEILLER", color: "#4a5568", bg: "#4a55681a" },
};

const COMPLIANCE = {
  faible:      { label: "Risque faible",       color: "#0a7a3d" },
  a_verifier:  { label: "À vérifier",           color: "#a16207" },
  obligatoire: { label: "Révision obligatoire", color: "#c92a2a" },
};


export default function GrowthRadar() {
  const [opps, setOpps] = useState([]);
  const [comparatives, setComparatives] = useState([]);
  const [otd, setOtd] = useState(null);
  const [providers, setProviders] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingComp, setGeneratingComp] = useState(false);
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterBadge, setFilterBadge] = useState("all");
  const [showProviders, setShowProviders] = useState(false);
  const [showCompConfig, setShowCompConfig] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [compConfig, setCompConfig] = useState({ territories: [], domains: [] });
  const [compDomains, setCompDomains] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [o, d, p, c, cfg, dom, h] = await Promise.all([
        api.get("/growth/radar/opportunities?limit=30"),
        api.get("/growth/radar/opportunity-of-the-day"),
        api.get("/growth/radar/providers"),
        api.get("/growth/radar/comparative/opportunities?limit=10"),
        api.get("/growth/radar/comparative/config"),
        api.get("/growth/radar/comparative/domains"),
        api.get("/growth/radar/health"),
      ]);
      setOpps(o.data.opportunities || []);
      setOtd(d.data.opportunity || null);
      setProviders(p.data.providers || []);
      setComparatives(c.data.opportunities || []);
      setCompConfig({
        territories: cfg.data.territories || [],
        domains: cfg.data.domains || [],
      });
      setCompDomains(dom.data.domains || []);
      setHealth(h.data || null);
    } catch (e) {
      toast.error("Chargement échoué");
    } finally { setLoading(false); }
  };

  const generateComparative = async () => {
    setGeneratingComp(true);
    toast.info("Analyse comparative en cours (Claude Sonnet 4.6)…");
    try {
      const { data } = await api.post("/growth/radar/comparative/generate?max_opportunities=3");
      toast.success(`${data.count} opportunité${data.count > 1 ? "s" : ""} comparative${data.count > 1 ? "s" : ""} générée${data.count > 1 ? "s" : ""}`);
      await load();
    } catch (e) {
      toast.error("Génération échouée");
    } finally { setGeneratingComp(false); }
  };

  const saveCompConfig = async () => {
    try {
      await api.post("/growth/radar/comparative/config", compConfig);
      toast.success("Configuration comparative sauvegardée");
      setShowCompConfig(false);
    } catch { toast.error("Échec sauvegarde"); }
  };

  const refresh = async () => {
    setRefreshing(true);
    toast.info("Détection en cours — ingestion + clustering + scoring…");
    try {
      const { data } = await api.post("/growth/radar/refresh", { include_demo: false });
      const total = Object.values(data.ingest || {}).reduce(
        (s, r) => s + (r.inserted || 0), 0);
      toast.success(`${total} nouveaux signaux · ${data.opportunities_count} opportunités`);
      await load();
    } catch (e) {
      toast.error("Refresh échoué");
    } finally { setRefreshing(false); }
  };

  const dismiss = async (oppId) => {
    try {
      await api.post(`/growth/radar/opportunities/${oppId}/dismiss`);
      toast.success("Opportunité ignorée");
      setOpps(prev => prev.filter(o => o.id !== oppId));
    } catch { toast.error("Échec"); }
  };

  useEffect(() => { load(); }, []);

  // ── Hiérarchie éditoriale stricte ──────────────────────────────
  // Le radar ne montre plus une liste brute — il propose UNE priorité,
  // 2-3 opportunités à considérer, et 1-2 comparaisons à fort potentiel.
  const editorial = useMemo(() => {
    const sorted = [...opps].sort((a, b) => (b.score || 0) - (a.score || 0));
    // Priorité #1 : Opportunity of the Day OU meilleure prioritaire
    const priority = otd || sorted.find(o => o.priority === "prioritaire") || sorted[0] || null;
    const priorityId = priority?.id;
    // Important : 2-3 opportunités suivantes (top score, jamais celle en priorité)
    const important = sorted
      .filter(o => o.id !== priorityId)
      .slice(0, 3);
    // Comparatives : top 2 par transferability_score
    const compSorted = [...comparatives].sort((a, b) =>
      ((b.comparative?.transferability_score || 0) - (a.comparative?.transferability_score || 0))
      || ((b.score || 0) - (a.score || 0)));
    const topComparatives = compSorted.slice(0, 2);
    return { priority, important, topComparatives };
  }, [opps, otd, comparatives]);

  const filtered = useMemo(() => opps.filter(o => {
    if (filterPriority !== "all" && o.priority !== filterPriority) return false;
    if (filterBadge !== "all" && o.badge !== filterBadge) return false;
    return true;
  }), [opps, filterPriority, filterBadge]);

  const activeProviders = providers.filter(p => p.api_key_configured && p.enabled);
  const scaffoldedProviders = providers.filter(p => p.requires_api_key && !p.api_key_configured);
  const healthSummary = health?.summary || null;
  const hasEditorial = editorial.priority || editorial.important.length > 0 || editorial.topComparatives.length > 0;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6"
          style={{ background: "#fbfaf5", minHeight: "100vh" }}>
      {/* ── Header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Radar size={24} style={{ color: CHAMPAGNE }} />
            <h1 className="text-3xl font-bold" style={{ color: NAVY }}
                data-testid="radar-title">
              Ce qui mérite votre attention
            </h1>
          </div>
          <p className="text-sm max-w-2xl" style={{ color: SLATE }}>
            Le professionnel ne cherche plus ses sujets — ALTIORA sélectionne les
            <b> quelques</b> signaux qui méritent votre prise de parole aujourd'hui.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={() => setShowProviders(v => !v)}
                    data-testid="btn-toggle-providers"
                    className="px-3 py-2 rounded text-sm flex items-center gap-2 border"
                    style={{ borderColor: "#0a254022", color: NAVY }}
                    title="Health Monitor — état des sources">
            <ShieldCheck size={14} />
            {healthSummary
              ? `${healthSummary.healthy}/${healthSummary.total} sources saines`
              : `${activeProviders.length} sources actives`}
          </button>
          <button onClick={refresh} disabled={refreshing}
                    data-testid="btn-radar-refresh"
                    className="px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"
                    style={{ background: NAVY, color: "white" }}>
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Détection…" : "Détecter maintenant"}
          </button>
        </div>
      </div>

      {/* ── Source Health Monitor (collapsible) ─────────────── */}
      {showProviders && (
        <Card testid="providers-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold" style={{ color: NAVY }}>
              Source Health Monitor
            </h3>
            {healthSummary && (
              <div className="flex gap-2 text-xs flex-wrap" data-testid="health-summary">
                <HealthPill color="#0a7a3d" label={`${healthSummary.healthy} saines`} />
                <HealthPill color="#a16207" label={`${healthSummary.degraded} dégradées`} />
                <HealthPill color="#c92a2a" label={`${healthSummary.blocked} bloquées`} />
                <HealthPill color="#6b7280" label={`${healthSummary.disabled} désactivées`} />
                {healthSummary.unknown > 0 && (
                  <HealthPill color="#4a5568" label={`${healthSummary.unknown} inconnues`} />
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {providers.map(p => <ProviderRow key={p.name} p={p} />)}
          </div>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════
          HIÉRARCHIE ÉDITORIALE STRICTE
          🔥 À traiter maintenant (1)
          🟢 À considérer aujourd'hui (2-3)
          🌍 Comparaison à fort potentiel (1-2)
          ═══════════════════════════════════════════════════════ */}

      {loading && (
        <div className="text-center py-12" style={{ color: SLATE }}
              data-testid="radar-loading">
          <RefreshCw className="animate-spin mx-auto mb-2" />
          Chargement…
        </div>
      )}

      {!loading && !hasEditorial && (
        <Card testid="empty-editorial">
          <div className="text-center py-10" style={{ color: SLATE }}>
            <Radar size={40} className="mx-auto mb-3" style={{ color: CHAMPAGNE, opacity: 0.6 }} />
            <p className="text-base font-semibold mb-1" style={{ color: NAVY }}>
              Aucune opportunité détectée pour l'instant.
            </p>
            <p className="text-sm">
              Cliquez sur <b>« Détecter maintenant »</b> pour actualiser depuis les sources connectées.
            </p>
          </div>
        </Card>
      )}

      {/* ── 🔥 À TRAITER MAINTENANT (max 1) ────────────────── */}
      {editorial.priority && (
        <section data-testid="section-a-traiter">
          <SectionTitle emoji="🔥" title="À TRAITER MAINTENANT"
              subtitle="Si vous ne publiez qu'une seule chose aujourd'hui"
              color="#c92a2a" />
          <OtdHero opp={editorial.priority} onDismiss={() => dismiss(editorial.priority.id)} />
        </section>
      )}

      {/* ── 🟢 À CONSIDÉRER AUJOURD'HUI (2-3) ──────────────── */}
      {editorial.important.length > 0 && (
        <section data-testid="section-a-considerer">
          <SectionTitle emoji="🟢" title="À CONSIDÉRER AUJOURD'HUI"
              subtitle="Sujets à fort potentiel pour cette semaine"
              color="#0a7a3d" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="editorial-important-grid">
            {editorial.important.map(o => (
              <OpportunityCard key={o.id} opp={o}
                                  expanded={expandedId === o.id}
                                  onToggle={() => setExpandedId(expandedId === o.id ? null : o.id)}
                                  onDismiss={() => dismiss(o.id)} />
            ))}
          </div>
        </section>
      )}

      {/* ── 🌍 COMPARAISON À FORT POTENTIEL (1-2) ──────────── */}
      <section data-testid="section-comparative">
        <div className="rounded-xl p-4"
              style={{ background: "linear-gradient(135deg, #0a7a3d08 0%, #c9a34e14 100%)",
                        border: "1px solid #c9a34e33" }}
              data-testid="comparative-section">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <div>
              <SectionTitle emoji="🌍" title="COMPARAISON À FORT POTENTIEL"
                  subtitle="OBSERVER → COMPARER → ADAPTER (territoires précis, jamais « Occident vs Afrique »)"
                  color="#c9a34e" inline />
              {compConfig.territories.length > 0 && (
                <div className="text-xs mt-1" style={{ color: SLATE }}>
                  Territoires : <b>{compConfig.territories.join(" · ")}</b>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCompConfig(v => !v)}
                        data-testid="btn-comparative-config"
                        className="px-3 py-1.5 rounded text-xs border"
                        style={{ borderColor: "#0a254022", color: NAVY }}>
                ⚙ Territoires & domaines
              </button>
              <button onClick={generateComparative} disabled={generatingComp}
                        data-testid="btn-comparative-generate"
                        className="px-3 py-1.5 rounded text-xs font-semibold"
                        style={{ background: "#0a7a3d", color: "white" }}>
                {generatingComp ? "Analyse…" : "🌍 Détecter comparatives"}
              </button>
            </div>
          </div>

          {showCompConfig && (
            <div className="mt-3 p-3 rounded bg-white border"
                  style={{ borderColor: "#0a254022" }}>
              <div className="text-xs font-semibold mb-2" style={{ color: NAVY }}>
                Territoires de référence (séparés par virgule)
              </div>
              <input type="text"
                      value={compConfig.territories.join(", ")}
                      onChange={e => setCompConfig(c => ({ ...c,
                          territories: e.target.value.split(",").map(t => t.trim()) }))}
                      data-testid="comp-territories-input"
                      placeholder="ex : Cameroun, Québec, Douala, France, Canada"
                      className="w-full px-3 py-1.5 border rounded text-sm mb-3"
                      style={{ borderColor: "#0a254022" }} />
              <div className="text-xs font-semibold mb-2" style={{ color: NAVY }}>
                Domaines prioritaires (cliquez pour toggler)
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {compDomains.map(d => {
                  const active = compConfig.domains.includes(d);
                  return (
                    <button key={d}
                      onClick={() => setCompConfig(c => ({
                        ...c,
                        domains: active
                          ? c.domains.filter(x => x !== d)
                          : [...c.domains, d]
                      }))}
                      className="px-2 py-0.5 rounded-full text-xs"
                      style={{
                        background: active ? "#0a7a3d" : "transparent",
                        color: active ? "white" : SLATE,
                        border: `1px solid ${active ? "#0a7a3d" : "#0a254033"}`,
                      }}>{d}</button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button onClick={saveCompConfig}
                          className="px-3 py-1.5 rounded text-xs font-semibold"
                          style={{ background: NAVY, color: "white" }}
                          data-testid="btn-comp-save">
                  Enregistrer
                </button>
                <button onClick={() => setShowCompConfig(false)}
                          className="px-3 py-1.5 rounded text-xs border"
                          style={{ borderColor: "#0a254022", color: SLATE }}>
                  Annuler
                </button>
              </div>
            </div>
          )}

          {editorial.topComparatives.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4"
                  data-testid="comparative-grid">
              {editorial.topComparatives.map(o => (
                <ComparativeCard key={o.id} opp={o} onDismiss={() => dismiss(o.id)} />
              ))}
            </div>
          )}
          {editorial.topComparatives.length === 0 && !generatingComp && (
            <div className="text-center py-4 text-xs mt-3" style={{ color: SLATE }}>
              Aucune comparaison à fort potentiel pour l'instant.
              Cliquez sur <b>« 🌍 Détecter comparatives »</b> pour lancer une analyse Claude.
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          ARCHIVES — Voir toutes les opportunités (opt-in)
          Masqué par défaut pour préserver la hiérarchie éditoriale.
          ═══════════════════════════════════════════════════════ */}
      {opps.length > 0 && (
        <div className="pt-2">
          <button onClick={() => setShowAll(v => !v)}
                    data-testid="btn-toggle-all-opps"
                    className="text-sm underline flex items-center gap-1"
                    style={{ color: SLATE }}>
            <ChevronRight size={14}
                className={`transition-transform ${showAll ? "rotate-90" : ""}`} />
            {showAll ? "Masquer" : "Voir"} toutes les opportunités ({opps.length})
          </button>

          {showAll && (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-semibold" style={{ color: SLATE }}>Filtres :</span>
                <FilterButton label="Tous" active={filterPriority === "all"}
                                onClick={() => setFilterPriority("all")} />
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <FilterButton key={k} label={v.label} color={v.color}
                                  active={filterPriority === k}
                                  onClick={() => setFilterPriority(k)}
                                  testid={`filter-priority-${k}`} />
                ))}
                <div className="w-px h-4 mx-1" style={{ background: "#0a254022" }} />
                <FilterButton label="Toutes origines" active={filterBadge === "all"}
                                onClick={() => setFilterBadge("all")} />
                {Object.entries(BADGES).map(([k, v]) => (
                  <FilterButton key={k} label={`${v.emoji} ${v.label}`} color={v.color}
                                  active={filterBadge === k}
                                  onClick={() => setFilterBadge(k)}
                                  testid={`filter-badge-${k}`} />
                ))}
              </div>
              {filtered.length === 0 ? (
                <Card testid="empty-state">
                  <div className="text-center py-8" style={{ color: SLATE }}>
                    <p className="text-sm">Aucune opportunité pour ces filtres.</p>
                  </div>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                      data-testid="all-opps-grid">
                  {filtered.map(o => (
                    <OpportunityCard key={o.id} opp={o}
                                        expanded={expandedId === o.id}
                                        onToggle={() => setExpandedId(expandedId === o.id ? null : o.id)}
                                        onDismiss={() => dismiss(o.id)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Sous-composants
// ═══════════════════════════════════════════════════════════════════

function SectionTitle({ emoji, title, subtitle, color, inline }) {
  return (
    <div className={inline ? "" : "mb-3"}>
      <div className="flex items-center gap-2">
        <span style={{ fontSize: 20 }}>{emoji}</span>
        <h2 className="text-lg font-bold tracking-tight"
            style={{ color: color || NAVY, letterSpacing: 0.3 }}>
          {title}
        </h2>
      </div>
      {subtitle && (
        <p className="text-xs mt-0.5 ml-8" style={{ color: SLATE }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function HealthPill({ color, label }) {
  return (
    <span className="px-2 py-0.5 rounded-full font-semibold"
          style={{ background: `${color}1a`, color, border: `1px solid ${color}55` }}>
      {label}
    </span>
  );
}

function ProviderRow({ p }) {
  const HEALTH_ICON = {
    healthy:   { icon: "✅", color: "#0a7a3d" },
    degraded:  { icon: "⚠️", color: "#a16207" },
    blocked:   { icon: "⛔", color: "#c92a2a" },
    disabled:  { icon: "⏸️", color: "#6b7280" },
    unknown:   { icon: "❓", color: "#4a5568" },
  };
  const hs = HEALTH_ICON[p.health] || HEALTH_ICON.unknown;
  return (
    <div className="flex items-start gap-3 p-2 rounded"
          style={{ background: p.health === "healthy" ? "#0a7a3d0a" : "#f5f5f5" }}
          data-testid={`provider-${p.name}`}>
      <span className="text-lg mt-0.5">{hs.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: INK }}>
          {p.display_name || p.name}
        </div>
        <div className="text-xs" style={{ color: SLATE }}>
          {p.source_type} · trust {p.trust_level} · <span style={{ color: hs.color }}>{p.health}</span>
          {p.last_fetch_count > 0 && (
            <span className="ml-1">· {p.last_fetch_count} signaux</span>
          )}
        </div>
        {p.last_error && (
          <div className="text-xs mt-0.5" style={{ color: hs.color }}
                data-testid={`provider-error-${p.name}`}>
            {p.last_error}
          </div>
        )}
        {p.requires_api_key && !p.api_key_configured && (
          <div className="text-xs mt-0.5" style={{ color: "#c92a2a" }}>
            nécessite {p.api_key_env}
          </div>
        )}
      </div>
    </div>
  );
}

function OtdHero({ opp, onDismiss }) {
  const badge = BADGES[opp.badge] || BADGES.live;
  const priority = PRIORITY[opp.priority] || PRIORITY.a_surveiller;
  const compliance = COMPLIANCE[opp.compliance_level] || COMPLIANCE.faible;
  return (
    <div className="rounded-xl p-6 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3557 100%)`,
                    color: "white" }}
          data-testid="otd-hero">
      <div className="absolute top-4 right-4 flex gap-2">
        <span className="px-2 py-1 rounded-full text-xs font-bold"
              style={{ background: CHAMPAGNE, color: NAVY }}>
          <Sparkles size={11} className="inline" /> OPPORTUNITÉ DU JOUR
        </span>
      </div>
      <div className="text-xs mb-2 opacity-80" style={{ color: CHAMPAGNE }}>
        Si vous ne publiez qu'une seule chose aujourd'hui :
      </div>
      <h2 className="text-2xl font-bold mb-3 pr-32" data-testid="otd-title">
        {opp.title}
      </h2>
      <div className="flex gap-2 mb-3">
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: badge.bg, color: badge.color,
                        border: `1px solid ${badge.color}55` }}
              data-testid="otd-badge">
          {badge.emoji} {badge.label}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: priority.bg, color: priority.color }}>
          {priority.label}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: "#ffffff22" }}>
          Score {opp.score}/100
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: "#ffffff22", color: compliance.color }}>
          {compliance.label}
        </span>
      </div>
      {opp.hook && (
        <p className="text-base mb-3 italic opacity-90"
            style={{ fontFamily: "Georgia, serif" }} data-testid="otd-hook">
          « {opp.hook} »
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
        {opp.why_now && (
          <div>
            <div className="text-xs mb-1 font-semibold" style={{ color: CHAMPAGNE }}>
              Pourquoi maintenant ?
            </div>
            <div className="opacity-90">{opp.why_now}</div>
          </div>
        )}
        {opp.business_objective && (
          <div>
            <div className="text-xs mb-1 font-semibold" style={{ color: CHAMPAGNE }}>
              Objectif business
            </div>
            <div className="opacity-90">{opp.business_objective}</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {opp.audiences?.map((a, i) => (
          <span key={i} className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: "#ffffff15" }}>
            👤 {a}
          </span>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button className="px-4 py-2 rounded font-semibold text-sm flex items-center gap-2"
                  style={{ background: CHAMPAGNE, color: NAVY }}
                  data-testid="otd-create">
          <Zap size={14} /> Créer maintenant
        </button>
        <button className="px-4 py-2 rounded text-sm border"
                  style={{ borderColor: "#ffffff44", color: "white" }}
                  data-testid="otd-sources">
          <ExternalLink size={12} className="inline mr-1" /> {opp.sources?.length || 0} sources
        </button>
        <button onClick={onDismiss}
                  className="px-4 py-2 rounded text-sm border"
                  style={{ borderColor: "#ffffff44", color: "white" }}
                  data-testid="otd-dismiss">
          Ignorer
        </button>
      </div>
    </div>
  );
}


function OpportunityCard({ opp, expanded, onToggle, onDismiss }) {
  const badge = BADGES[opp.badge] || BADGES.live;
  const priority = PRIORITY[opp.priority] || PRIORITY.a_surveiller;
  const compliance = COMPLIANCE[opp.compliance_level] || COMPLIANCE.faible;
  const windowLeft = Math.max(0, opp.window_hours || 72);
  return (
    <Card testid={`opp-card-${opp.id}`}>
      {/* Score + badge + priorité */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="text-2xl font-bold" style={{ color: NAVY }}
              data-testid="opp-score">
          {opp.score}
          <span className="text-xs opacity-60">/100</span>
        </div>
        <div className="flex-1" />
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: badge.bg, color: badge.color,
                        border: `1px solid ${badge.color}55` }}
              title="Origine du signal">
          {badge.emoji}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: priority.bg, color: priority.color }}>
          {priority.label}
        </span>
      </div>

      {/* Titre */}
      <h4 className="font-semibold text-sm mb-2 leading-snug" style={{ color: INK }}
          data-testid="opp-title">
        {opp.title}
      </h4>

      {/* Hook */}
      {opp.hook && (
        <p className="text-xs italic mb-2" style={{ color: SLATE }}>
          « {opp.hook.slice(0, 140)}{opp.hook.length > 140 ? "…" : ""} »
        </p>
      )}

      {/* Meta */}
      <div className="flex flex-wrap gap-1 mb-2 text-xs">
        <span style={{ color: SLATE }}>⏱ {windowLeft}h de fenêtre</span>
        <span style={{ color: SLATE }}>· 📚 {opp.sources?.length || 0} sources</span>
        {opp.best_angle && (
          <span style={{ color: SLATE }}>· 📝 {opp.best_angle.replace(/_/g, " ")}</span>
        )}
      </div>

      {/* Compliance flag */}
      <div className="text-xs mb-2 inline-block px-2 py-0.5 rounded"
            style={{ color: compliance.color, background: `${compliance.color}0a` }}>
        <ShieldCheck size={10} className="inline mr-1" />
        {compliance.label}
      </div>

      {/* Expansion : reason + why_now + audiences + sources */}
      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-2 text-xs"
              style={{ borderColor: "#0a254022", color: SLATE }}>
          {opp.reason && <div><b>Raison :</b> {opp.reason}</div>}
          {opp.why_now && <div><b>Maintenant :</b> {opp.why_now}</div>}
          {opp.audiences?.length > 0 && (
            <div>
              <b>Audiences :</b>
              <ul className="mt-1 list-disc list-inside">
                {opp.audiences.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {opp.sources?.length > 0 && (
            <div>
              <b>Sources ({opp.sources.length}) :</b>
              <ul className="mt-1 space-y-1">
                {opp.sources.slice(0, 5).map((s, i) => (
                  <li key={i}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noreferrer"
                          className="underline" style={{ color: NAVY }}>
                        {s.name || s.url} <ExternalLink size={9} className="inline" />
                      </a>
                    ) : (s.name || "Source interne")}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Score breakdown */}
          {opp.breakdown && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold" style={{ color: NAVY }}>
                Détail du score
              </summary>
              <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                {Object.entries(opp.breakdown)
                       .filter(([k, v]) => typeof v === "number" && v > 0)
                       .map(([k, v]) => (
                  <div key={k}>
                    {k.replace(/_score$/, "").replace(/_/g, " ")} : <b>{v}</b>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1 mt-3">
        <button className="flex-1 px-2 py-1.5 rounded text-xs font-semibold"
                  style={{ background: NAVY, color: "white" }}
                  data-testid={`opp-create-${opp.id}`}>
          <Zap size={11} className="inline mr-1" /> Créer
        </button>
        <button onClick={onToggle}
                  className="px-2 py-1.5 rounded text-xs border"
                  style={{ borderColor: "#0a254022", color: NAVY }}
                  data-testid={`opp-expand-${opp.id}`}>
          {expanded ? "Réduire" : "Analyse"}
        </button>
        <button onClick={onDismiss}
                  className="px-2 py-1.5 rounded text-xs border"
                  style={{ borderColor: "#0a254022", color: SLATE }}
                  data-testid={`opp-dismiss-${opp.id}`}>
          <X size={11} />
        </button>
      </div>
    </Card>
  );
}


function FilterButton({ label, active, onClick, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
              className="px-2.5 py-1 rounded-full text-xs font-semibold transition"
              style={{
                background: active ? (color || NAVY) : "transparent",
                color: active ? "white" : (color || SLATE),
                border: `1px solid ${active ? (color || NAVY) : "#0a254033"}`,
              }}>
      {label}
    </button>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Comparative Opportunity Card — 🌍
// ═══════════════════════════════════════════════════════════════════

const CATEGORIES = {
  really_performant: { label: "Réellement performant", color: "#0a7a3d", icon: "✓" },
  transferable:      { label: "Transférable",           color: "#c9a34e", icon: "↺" },
  adaptable:         { label: "Adaptable",              color: "#a16207", icon: "⟳" },
  different:         { label: "Simplement différent",   color: "#4a5568", icon: "≠" },
  not_applicable:    { label: "Non applicable",         color: "#c92a2a", icon: "✕" },
};

function ComparativeCard({ opp, onDismiss }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const c = opp.comparative || {};
  const cat = CATEGORIES[c.category] || CATEGORIES.adaptable;

  const generateContent = async (fmt) => {
    setGenerating(true);
    toast.info(`Génération ${fmt} en cours (Claude Sonnet 4.6)…`);
    try {
      const { data } = await api.post(
        `/growth/radar/opportunities/${opp.id}/generate-content`,
        { format: fmt });
      setGenerated(data);
      const level = data.compliance?.level_label || "OK";
      toast.success(`${fmt} généré · Compliance : ${level}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec génération");
    } finally { setGenerating(false); }
  };
  return (
    <div className="bg-white rounded-lg p-4 border relative"
          style={{ borderColor: "#0a7a3d33" }}
          data-testid={`comp-card-${opp.id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="text-2xl">🌍</div>
        <div className="flex-1">
          <div className="text-xs font-semibold mb-1" style={{ color: "#0a7a3d" }}>
            OPPORTUNITÉ COMPARATIVE · {opp.score}/100
          </div>
          <h4 className="font-bold text-sm leading-snug" style={{ color: NAVY }}
              data-testid="comp-title">
            {opp.title}
          </h4>
        </div>
      </div>

      {c.country_a && c.country_b && (
        <div className="text-xs mb-2 px-2 py-1 rounded font-semibold"
              style={{ background: "#0a7a3d0a", color: NAVY }}
              data-testid="comp-countries">
          🇨🇦 {c.country_a}  ↔  🇨🇲 {c.country_b}
        </div>
      )}

      <div className="mb-2 inline-block px-2 py-0.5 rounded text-xs font-semibold"
            style={{ background: `${cat.color}1a`, color: cat.color,
                      border: `1px solid ${cat.color}55` }}>
        {cat.icon} {cat.label}
      </div>

      {opp.hook && (
        <p className="text-xs italic mb-2" style={{ color: SLATE }}>
          « {opp.hook} »
        </p>
      )}

      {c.practice && (
        <div className="text-xs mb-2">
          <b style={{ color: NAVY }}>🇨🇦 Pratique observée : </b>
          <span style={{ color: SLATE }}>{c.practice}</span>
        </div>
      )}
      {c.problem && (
        <div className="text-xs mb-2">
          <b style={{ color: NAVY }}>🌍 Problème traité : </b>
          <span style={{ color: SLATE }}>{c.problem}</span>
        </div>
      )}
      {c.what_transferable && (
        <div className="text-xs mb-2">
          <b style={{ color: "#0a7a3d" }}>💡 Ce qui pourrait être transférable : </b>
          <span style={{ color: SLATE }}>{c.what_transferable}</span>
        </div>
      )}
      {c.what_not_transferable && (
        <div className="text-xs mb-2">
          <b style={{ color: "#c92a2a" }}>⚠️ Contraintes locales : </b>
          <span style={{ color: SLATE }}>{c.what_not_transferable}</span>
        </div>
      )}
      {c.africa_relevance && (
        <div className="text-xs mb-3 p-2 rounded"
              style={{ background: "#c9a34e0a", color: SLATE }}>
          <b style={{ color: "#a16207" }}>🌍 Pertinence africaine : </b>
          {c.africa_relevance}
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs mb-3">
        <span style={{ color: SLATE }}>
          📊 Impact <b>{opp.score}/100</b>
        </span>
        <span style={{ color: SLATE }}>
          · 🌍 Transférabilité <b>{c.transferability_score}/100</b>
        </span>
        <span style={{ color: SLATE }}>
          · 🎓 Éducatif <b>{c.educational_value}/100</b>
        </span>
        <span style={{ color: SLATE }}>
          · 🔥 Conversation <b>{c.conversation_potential}/100</b>
        </span>
      </div>

      {c.evidence && c.evidence.length > 0 && (
        <details className="text-xs mb-2" style={{ color: SLATE }}>
          <summary className="cursor-pointer font-semibold" style={{ color: NAVY }}>
            📚 {c.evidence.length} preuve{c.evidence.length > 1 ? "s" : ""}
          </summary>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {c.evidence.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </details>
      )}

      <div className="flex gap-1 flex-wrap">
        <div className="relative">
          <button disabled={generating}
                    onClick={() => generateContent("linkedin_post")}
                    className="px-2 py-1 rounded text-xs font-semibold"
                    style={{ background: NAVY, color: "white" }}
                    data-testid={`comp-create-${opp.id}`}>
            <Zap size={11} className="inline mr-1" />
            {generating ? "Génération…" : "Post LinkedIn"}
          </button>
        </div>
        <button disabled={generating}
                  onClick={() => generateContent("carousel")}
                  className="px-2 py-1 rounded text-xs border"
                  style={{ borderColor: "#0a254022", color: NAVY }}>
          Carrousel
        </button>
        <button disabled={generating}
                  onClick={() => generateContent("video_script")}
                  className="px-2 py-1 rounded text-xs border"
                  style={{ borderColor: "#0a254022", color: NAVY }}>
          Script vidéo
        </button>
        <button disabled={generating}
                  onClick={() => generateContent("article")}
                  className="px-2 py-1 rounded text-xs border"
                  style={{ borderColor: "#0a254022", color: NAVY }}>
          Article
        </button>
        <button onClick={onDismiss}
                  className="px-2 py-1 rounded text-xs border"
                  style={{ borderColor: "#0a254022", color: SLATE }}
                  data-testid={`comp-dismiss-${opp.id}`}>
          <X size={11} />
        </button>
      </div>

      {/* Contenu généré + compliance badge */}
      {generated && (
        <div className="mt-3 p-2 rounded border-t"
              style={{ borderColor: "#0a254022" }}
              data-testid={`comp-generated-${opp.id}`}>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="font-semibold" style={{ color: NAVY }}>
              📄 {generated.format} généré
            </span>
            {(() => {
              const lvl = generated.compliance?.level;
              const lc = { faible: "#0a7a3d", a_verifier: "#a16207", obligatoire: "#c92a2a" }[lvl] || "#4a5568";
              return (
                <span className="px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `${lc}1a`, color: lc,
                                  border: `1px solid ${lc}55` }}
                        data-testid="comp-compliance-badge">
                  🛡 {generated.compliance?.level_label}
                  {generated.compliance?.flag_count > 0 &&
                    ` (${generated.compliance.flag_count})`}
                </span>
              );
            })()}
          </div>
          <div className="text-xs whitespace-pre-wrap p-2 rounded max-h-64 overflow-y-auto"
                style={{ background: "#fafaf7", color: SLATE }}>
            {generated.content}
          </div>
          {generated.compliance?.flags?.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer" style={{ color: "#c92a2a" }}>
                ⚠️ {generated.compliance.flags.length} avertissement{generated.compliance.flags.length > 1 ? "s" : ""} compliance
              </summary>
              <ul className="mt-1 list-disc list-inside" style={{ color: SLATE }}>
                {generated.compliance.flags.slice(0, 5).map((f, i) => (
                  <li key={i}>{f.category} : "{f.match}"</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
